import { useEffect, useRef, useMemo, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

function Particles() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const particles = useMemo(
    () =>
      Array.from({ length: isMobile ? 8 : 30 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        size: 1 + Math.random() * 2,
        duration: 4 + Math.random() * 6,
        delay: Math.random() * 5,
        opacity: 0.3 + Math.random() * 0.5,
      })),
    [isMobile]
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: p.left,
            bottom: "-5%",
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            opacity: p.opacity,
          }}
        />
      ))}
    </div>
  );
}

function SpeedStreaks() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const streaks = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => ({
        id: i,
        top: `${10 + Math.random() * 80}%`,
        width: `${100 + Math.random() * 300}px`,
        duration: 1.5 + Math.random() * 2,
        delay: Math.random() * 3,
      })),
    []
  );

  if (isMobile) return null;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
      {streaks.map((s) => (
        <div
          key={s.id}
          className="speed-streak"
          style={{
            top: s.top,
            width: s.width,
            left: "0",
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

const headlineWords = ["WHERE", "SPEED", "MEETS", "THRILL"];

export default function Hero() {
  const ref = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const textY = useTransform(scrollYProgress, [0, 1], isMobile ? ["0%", "20%"] : ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section
      ref={ref}
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black"
    >
      {/* Video Background */}
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src="/hero-video.webm"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/60 to-black/30 z-[1]" />

      {/* Particles */}
      <Particles />

      {/* Speed Streaks */}
      <SpeedStreaks />

      {/* Noise */}
      <div className="absolute inset-0 noise-overlay z-[2]" />

      {/* Content with parallax */}
      <motion.div
        className="relative z-10 text-center px-6 max-w-5xl"
        style={{ y: textY, opacity }}
      >
        <motion.span
          className="inline-block text-xs sm:text-sm uppercase tracking-[0.3em] text-[#e8001d] font-bold mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Dehradun&apos;s Biggest Home of Speed &amp; Play
        </motion.span>

        {/* Animated headline - word by word */}
        <div className="overflow-hidden mb-6">
          <div className="flex flex-wrap justify-center gap-x-4 sm:gap-x-6">
            {headlineWords.map((word, i) => (
              <motion.span
                key={word}
                className={`font-heading text-6xl sm:text-8xl lg:text-9xl uppercase leading-none inline-block ${i >= 2 ? "text-[#e8001d]" : "text-[#f5f5f5]"
                  }`}
                initial={{ y: "120%", rotateX: 40 }}
                animate={{ y: "0%", rotateX: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.4 + i * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {word}
              </motion.span>
            ))}
          </div>
        </div>

        <motion.p
          className="text-[#a3a3a3] text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          Dehradun&apos;s First Go-Karting Experience. Speeds up to 55 km/h
          on a sprawling track with long straights and technical turns.
        </motion.p>
      </motion.div>

      {/* Bottom racing stripe */}
      <div className="absolute bottom-0 left-0 right-0 h-1 z-10 flex">
        <div className="flex-1 bg-[#e8001d]" />
        <div className="flex-1 bg-transparent" />
        <div className="flex-1 bg-[#e8001d]" />
        <div className="flex-1 bg-transparent" />
        <div className="flex-1 bg-[#e8001d]" />
      </div>
    </section>
  );
}
